import {
  useCreateCheckoutSession,
  useCustomerPortal,
} from "@/api/hooks/use-subscription.ts";
import { useCallback, useState } from "react";
import { usePricingStore } from "../store/pricing-store";
import { PricingPlan } from "@/api/models/api-types";
import { usePaymentCore } from "@/features/payment";
import { useToast } from "@/features/toast";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import { pushDataLayer } from "@/lib/analytics";

/**
 * `begin_checkout`, tıklamada değil Stripe oturumu gerçekten açıldığında
 * gönderilir — tıklayıp vazgeçen veya hata alan kullanıcı huniye girmemeli.
 *
 * Değer olarak `usdPrices` kullanılıyor: `discountedPrice` bölgeye göre
 * çevrilmiş GÖSTERİM fiyatı, tahsilat ise her zaman USD. İkisini karıştırmak
 * USD değerli bir dönüşüme çevrilmiş lira yazmak olurdu.
 */
const pushBeginCheckout = (plan: PricingPlan, sessionId?: string) => {
  pushDataLayer({
    event: "begin_checkout",
    value: plan.usdPrices?.discountedPrice ?? plan.discountedPrice,
    currency: "USD",
    checkout_session_id: sessionId,
    items: [
      {
        item_id: plan._id,
        item_name: plan.name,
      },
    ],
  });
};

export const usePricingActions = () => {
  const {
    selectedPlan,
    selectedBillingCycle,
    setSelectedPlan,
    setSelectedBillingCycle,
    openPricingModal,
    openPricingModalWithTopup,
    closePricingModal,
    resetPricing,
  } = usePricingStore();

  const { createCheckoutSession } = useCreateCheckoutSession();
  const { getCustomerPortalUrl } = useCustomerPortal();
  const { startPayment } = usePaymentCore();
  const { error: showErrorToast } = useToast();
  const { data: billingData } = useBillingHistory();

  // Loading state for checkout session creation
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const selectPlan = useCallback(
    (plan: PricingPlan) => {
      setSelectedPlan(plan);
    },
    [setSelectedPlan],
  );

  const changeBillingCycle = useCallback(
    (cycle: "monthly" | "yearly" | "one-time") => {
      setSelectedBillingCycle(cycle);
    },
    [setSelectedBillingCycle],
  );

  const handleSubscribe = useCallback(
    async (plan: PricingPlan) => {
      try {
        setLoadingPlanId(plan._id);

        // 🔍 Pricing plan kontrolü - kullanıcı free plan'da mı?
        const pricingPlan = billingData?.data?.pricingPlan;
        const subscription = billingData?.data?.subscription;

        // Eğer mevcut plan free değilse ve iptal (ücretli bir plan varsa), customer portal'a yönlendir
        if (
          pricingPlan &&
          !pricingPlan.isFree &&
          subscription &&
          subscription.status != "canceled"
        ) {
          const portalResponse = await getCustomerPortalUrl({
            subscriptionId: subscription._id,
            targetPricingPlanId: plan._id,
          });

          if (portalResponse?.data?.url) {
            // Customer portal URL'e navigate et
            // Not: closePricingModal() çağrılmaz — window.location.href ile
            // dış URL'e gidildiğinde navigate(-1) bu yönlendirmeyi iptal eder.
            window.location.href = portalResponse.data.url;
            return;
          }
        }

        // Normal checkout akışı - free user veya yeni subscription için
        const result = await createCheckoutSession({ planId: plan._id });
        pushBeginCheckout(plan, result?.data?.sessionId);
        // Yeni checkout sistemini kullan
        startPayment("stripe", result?.data?.sessionId, result?.data?.url);
        closePricingModal();
      } catch (errorResponse: any) {
        const backendErrorMessage =
          errorResponse?.response?.data?.code ||
          errorResponse?.response?.data?.message ||
          errorResponse?.message ||
          "Beklenmeyen bir hata oluştu";
        showErrorToast(backendErrorMessage);
      } finally {
        setLoadingPlanId(null);
      }
    },
    [
      createCheckoutSession,
      getCustomerPortalUrl,
      billingData,
      closePricingModal,
      showErrorToast,
      startPayment,
    ],
  );

  const handleBuyCredits = useCallback(
    async (plan: PricingPlan) => {
      try {
        setLoadingPlanId(plan._id);
        const result = await createCheckoutSession({ planId: plan._id });
        pushBeginCheckout(plan, result?.data?.sessionId);
        // Yeni payment sistemini kullan
        startPayment("stripe", result?.data?.sessionId, result?.data?.url);
        closePricingModal();
      } catch (errorResponse: any) {
        const backendErrorMessage =
          errorResponse?.response?.data?.code ||
          errorResponse?.response?.data?.message ||
          errorResponse?.message ||
          "Beklenmeyen bir hata oluştu";
        showErrorToast(backendErrorMessage);
      } finally {
        setLoadingPlanId(null);
      }
    },
    [createCheckoutSession, closePricingModal, showErrorToast, startPayment],
  );

  return {
    selectedPlan,
    selectedBillingCycle,
    selectPlan,
    changeBillingCycle,
    openPricingModal,
    openPricingModalWithTopup,
    closePricingModal,
    resetPricing,
    handleSubscribe,
    handleBuyCredits,
    loadingPlanId,
  };
};
